import { useMediaQuery, useTheme } from '@mui/material';
import { AdvancedMarker, ColorScheme, Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { COLORS } from './constants';
import { ShimmerLine } from './LoadingAnimations';

import { HauserEQNearestPOIs, HauserEQPOIItem } from '@/services/api/hauser-eq/api';
import { GOOGLE_MAPS_MAP_ID } from '@/types/maps';

const NEIGHBORHOOD_MAP_ID = 'neighborhood-map-card';

// --- Types ---


interface RadiusPOI {
  name: string;
  category: 'emergency' | 'hazard' | 'lifestyle';
  group: string;
  lat?: number;
  lng?: number;
  type: string;
  distanceMi: number;
  color: string;
}

// --- Constants ---

const POI_COLORS: Record<string, string> = {
  school: '#0099cc',
  college: '#6b5ce7',
  hospital: '#4a9e3f',
  fire: '#d4772c',
  police: '#b03060',
  ambulance: '#2a9d8f',
  hazard: '#c0392b',
};

// --- Helpers ---

function poiDistanceMi(item: HauserEQPOIItem): number {
  const d = item.distance;
  if (!d) return 0;
  const val = d.value ?? 0;
  const unit = (d.unit ?? 'Miles').toLowerCase();
  if (unit === 'feet') return val / 5280;
  if (unit === 'kilometers') return val * 0.621371;
  if (unit === 'meters') return val * 0.000621371;
  return val;
}

// --- Props ---

export interface NeighborhoodMapCardProps {
  center: { latitude: number; longitude: number };
  propertyAddress: string;
  isLoading?: boolean;
  nearestPointsOfInterest?: HauserEQNearestPOIs | null;
  bare?: boolean;
  /** 27": fill the height it's given, the map taking all of it above the neighborhood row. */
  fill?: boolean;
}

// --- Sub-components ---

// A place on the map, Tradehaus's dot (mapCore markSprite): a near-solid body in its colour, the faintest top-left
// sheen, a fine ring in a lighter shade of its own colour and a soft shadow; hazards are drawn as a ring
const POIMarker = ({ color, hollow = false, active = false }: { color: string; hollow?: boolean; active?: boolean }) => (
  <div
    style={{
      width: active ? 14 : 12,
      height: active ? 14 : 12,
      borderRadius: '50%',
      background: hollow ? 'transparent' : `color-mix(in srgb, ${color} 86%, transparent)`,
      backgroundImage: hollow
        ? 'none'
        : 'linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.06) 55%, rgba(255,255,255,0) 100%)',
      boxShadow: `inset 0 0 0 ${hollow ? 2 : 1.25}px color-mix(in srgb, ${color} ${hollow ? 100 : 65}%, white), 0 1.5px 6px rgba(38, 38, 38, 0.22)`,
      transition: 'width 0.12s ease, height 0.12s ease',
    }}
  />
);

// The property: a white dot in a soft ring, the one mark that is not a category
const PropertyMarker = () => (
  <div
    style={{
      width: 14,
      height: 14,
      borderRadius: '50%',
      background: '#f7f7f6',
      boxShadow: '0 0 0 4px rgba(247, 247, 246, 0.22), 0 2px 8px rgba(0, 0, 0, 0.4)',
    }}
  />
);

// Tracks whether the active map type is satellite/hybrid (dark imagery)
// vs roadmap (light) so we can flip the control labels/icons accordingly.
function MapTypeListener({ onChange }: { onChange: (isDark: boolean) => void }) {
  const map = useMap(NEIGHBORHOOD_MAP_ID);
  useEffect(() => {
    if (!map) return undefined;
    const update = () => {
      const t = map.getMapTypeId();
      onChange(t === 'satellite' || t === 'hybrid');
    };
    update();
    const listener = map.addListener('maptypeid_changed', update);
    return () => listener.remove();
  }, [map, onChange]);
  return null;
}

// Draws the static 0.5mi/1mi rings and optional hovered POI ring using Google Maps Circle
function MapRings({
  center,
  hoveredPOI,
}: {
  center: { latitude: number; longitude: number };
  hoveredPOI: RadiusPOI | null;
}) {
  const map = useMap(NEIGHBORHOOD_MAP_ID);
  const mapsLib = useMapsLibrary('maps');

  useEffect(() => {
    if (!map || !mapsLib) return;
    const circles = [804, 1609].map(
      (radius) =>
        new mapsLib.Circle({
          map,
          center: { lat: center.latitude, lng: center.longitude },
          radius,
          strokeColor: 'rgba(200,195,188,0.7)',
          strokeOpacity: 0.4,
          strokeWeight: 1,
          fillOpacity: 0,
        }),
    );
    return () => circles.forEach((c) => c.setMap(null));
  }, [map, mapsLib, center.latitude, center.longitude]);

  useEffect(() => {
    if (!map || !mapsLib || !hoveredPOI) return;
    const circle = new mapsLib.Circle({
      map,
      center: { lat: center.latitude, lng: center.longitude },
      radius: hoveredPOI.distanceMi * 1609.34,
      strokeColor: hoveredPOI.color,
      strokeOpacity: 0.7,
      strokeWeight: 2,
      fillOpacity: 0,
    });
    return () => circle.setMap(null);
  }, [map, mapsLib, center.latitude, center.longitude, hoveredPOI]);

  return null;
}

// --- Main Component ---

export const NeighborhoodMapCard = ({
  center,
  propertyAddress,
  isLoading = false,
  nearestPointsOfInterest,
  bare = false,
  fill = false,
}: NeighborhoodMapCardProps) => {
  const [hoveredRadius, setHoveredRadius] = useState<string | null>(null);
  const [isSatelliteView, setIsSatelliteView] = useState(false);
  const [listOpen, setListOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // what's nearby, in the order a buyer weighs it: each group's nearest few. Lifestyle places come from Google Places
  // (seeded with the property), schools and services from PropertyLens
  const radiusPOIs = useMemo<RadiusPOI[]>(() => {
    if (!nearestPointsOfInterest) return [];
    const n = nearestPointsOfInterest;
    const GROUPS: { group: string; category: RadiusPOI['category']; color: string; sources: (HauserEQPOIItem[] | undefined)[] }[] = [
      { group: 'Schools', category: 'lifestyle', color: POI_COLORS.school, sources: [n.public_schools, n.private_schools, n.schools, n.childcare_centers] },
      { group: 'Everyday', category: 'lifestyle', color: '#d6d2c9', sources: [n.groceries, n.pharmacies, n.cafes, n.restaurants] },
      { group: 'Outdoors & community', category: 'lifestyle', color: '#7fb069', sources: [n.parks, n.libraries, n.gyms] },
      { group: 'Shopping & transit', category: 'lifestyle', color: '#bbb7af', sources: [n.shopping_malls, n.department_stores, n.transit] },
      {
        group: 'Safety',
        category: 'emergency',
        color: POI_COLORS.fire,
        sources: [n.fire_stations, n.police_stations, n.law_enforcement_facilities, n.ambulance_services],
      },
      {
        group: 'Environmental',
        category: 'hazard',
        color: POI_COLORS.hazard,
        sources: [n.superfund_sites, n.brownfields, n.toxic_release_facilities, n.underground_storage_tanks, n.asbestos_facilities],
      },
    ];
    const result: RadiusPOI[] = [];
    for (const g of GROUPS) {
      const seen = new Set<string>();
      const items = g.sources
        .flatMap((list) => list ?? [])
        .filter((item) => item?.name && !seen.has(item.name) && seen.add(item.name))
        .map((item) => {
          const at = item.coordinates ?? item.location;
          return {
            name: item.name,
            type: g.group,
            group: g.group,
            category: g.category,
            distanceMi: poiDistanceMi(item),
            color: g.color,
            lat: at?.latitude,
            lng: at?.longitude,
          };
        })
        .sort((a, b) => a.distanceMi - b.distanceMi)
        .slice(0, 3);
      result.push(...items);
    }
    return result;
  }, [nearestPointsOfInterest]);


  const hoveredPOI = hoveredRadius
    ? radiusPOIs.find((p) => `${p.type}-${p.name}` === hoveredRadius) ?? null
    : null;

  if (isLoading) {
    return (
      <div
        style={{
          ...(!bare && {
            background: COLORS.card,
            borderRadius: 25,
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
          }),
          overflow: 'hidden',
        }}>
        {!bare && (
          <div
            style={{
              padding: '20px 20px 14px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <div style={{ fontSize: 17, fontWeight: 300, color: COLORS.text }}>
              Neighborhood map
            </div>
            <ShimmerLine width={160} height={14} />
          </div>
        )}
        <div
          style={{
            margin: bare ? 0 : '0 20px',
            borderRadius: bare ? 0 : 18,
            overflow: 'hidden',
            height: 380,
          }}>
          <ShimmerLine width="100%" height="100%" borderRadius={0} />
        </div>
        <div style={{ padding: '12px 20px 20px', display: 'flex', gap: 10 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <ShimmerLine key={i} width={70} height={24} borderRadius={10} delay={i * 0.1} />
          ))}
        </div>
      </div>
    );
  }


  return (
    <div
      style={{
        ...(!bare && {
          background: COLORS.card,
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          borderRadius: '24px',
        }),
        overflow: 'hidden',
        position: 'relative',
        ...(fill && !isMobile && { height: '100%', display: 'flex', flexDirection: 'column' }),
        ...(isMobile && {
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 20,
          boxSizing: 'border-box',
        }),
      }}>
      {/* no title: the map says what it is, and runs to the card's top edge */}
      <style>{`
        /* The base map recedes so the places pop: its tiles go grey and lower-contrast (a Map ID blocks custom map
           styles, so the tone is set on the tiles themselves; markers are a separate layer and keep full colour) */
        .hx-map-tone .gm-style canvas,
        .hx-map-tone .gm-style div > img:not([src*="svg"]) {
          filter: saturate(0) contrast(0.5) brightness(0.75);
        }
        /* Light frosted glass + 10px radius on built-in Google Maps
           control buttons. Inner label/icon color flips by map type:
           dark (#333) on Map view, light (#f3f3f3) on Satellite.
           Pegman intentionally left as Google's default — overriding it
           with a CSS background broke its native drag-to-Street-View. */
        .gm-style .gm-style-mtc > button,
        .gm-style .gm-fullscreen-control,
        .gm-style .gm-svpc,
        .gm-style .gm-control-active {
          border-radius: 10px !important;
          background: rgba(255,255,255,0.25) !important;
          backdrop-filter: blur(12px) saturate(160%) !important;
          -webkit-backdrop-filter: blur(12px) saturate(160%) !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.18) !important;
          color: ${isSatelliteView ? '#f3f3f3' : '#333333'} !important;
        }
        .gm-style .gm-style-mtc > button {
          font-family: 'Manrope', sans-serif !important;
          font-size: 16px !important;
          font-weight: 400 !important;
          letter-spacing: -0.01em !important;
          text-transform: none !important;
        }
        /* Selected Map/Satellite button — solid dark fill with light label. */
        .gm-style .gm-style-mtc > button[aria-checked="true"],
        .gm-style .gm-style-mtc > button[aria-pressed="true"] {
          background: #333333 !important;
          color: #f3f3f3 !important;
        }
        .gm-style .gm-style-mtc ul li,
        .gm-style .gm-style-mtc ul li *,
        .gm-style .gm-style-mtc-menu * {
          font-family: 'Manrope', sans-serif !important;
        }
        .gm-style .gm-style-mtc > button svg,
        .gm-style .gm-fullscreen-control svg,
        .gm-style .gm-control-active svg {
          fill: ${isSatelliteView ? '#f3f3f3' : '#333333'} !important;
          stroke: ${isSatelliteView ? '#f3f3f3' : '#333333'} !important;
        }
        .gm-style .gm-fullscreen-control img,
        .gm-style .gm-control-active img {
          filter: ${isSatelliteView ? 'brightness(0) invert(1)' : 'brightness(0)'} !important;
        }
        /* Pegman: keep Google's native img (drag-to-Street-View intact) but
           desaturate the yellow figure to neutral grey, biased dark on Map
           view and light on Satellite view to match the icon flip. */
        .gm-style .gm-svpc img {
          filter: ${
            isSatelliteView ? 'grayscale(1) brightness(1.4)' : 'grayscale(1) brightness(0.6)'
          } !important;
        }
        /* 5px gap between the Map and Satellite buttons. */
        .gm-style .gm-style-mtc + .gm-style-mtc {
          margin-left: 5px !important;
        }
        /* Map type dropdown panel (Terrain checkbox menu) — same frosted
           glass + 10px radius + 11px label text as the parent buttons. */
        .gm-style .gm-style-mtc ul,
        .gm-style .gm-style-mtc-menu {
          border-radius: 10px !important;
          background: rgba(255,255,255,0.25) !important;
          backdrop-filter: blur(12px) saturate(160%) !important;
          -webkit-backdrop-filter: blur(12px) saturate(160%) !important;
          box-shadow: 0 4px 14px rgba(0,0,0,0.2) !important;
          padding: 4px !important;
          border: none !important;
          /* Align the dropdown's left edge with the parent button. */
          left: 0 !important;
          right: auto !important;
        }
        .gm-style .gm-style-mtc ul li,
        .gm-style .gm-style-mtc ul li *,
        .gm-style .gm-style-mtc-menu * {
          font-size: 12px !important;
          color: #333333 !important;
        }
        .gm-style .gm-style-mtc ul li {
          border-radius: 6px !important;
          display: flex !important;
          align-items: center !important;
          gap: 2.5px !important;
        }
        .gm-style .gm-style-mtc ul li > * + * {
          margin-left: 2.5px !important;
        }
      `}</style>
      <div
        className="hx-map-tone"
        style={{
          // full frame: the map runs to the card's edges; title and legend float over it
          overflow: 'hidden',
          height: isMobile || fill ? undefined : 420,
          // phone: the map takes the whole card until the places are opened, then shares it two to one
          flex: isMobile ? (listOpen ? 2 : 1) : fill ? 1 : undefined,
          minHeight: isMobile ? 0 : fill ? 420 : undefined,
          position: 'relative',
        }}>


        {!import.meta.env.VITE_GOOGLE_MAPS_API_KEY ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: COLORS.cardAlt ?? COLORS.card,
            }}>
            <span style={{ color: COLORS.textDim, fontSize: 13 }}>
              Map unavailable: VITE_GOOGLE_MAPS_API_KEY not configured
            </span>
          </div>
        ) : (
          <Map
            id={NEIGHBORHOOD_MAP_ID}
            mapId={GOOGLE_MAPS_MAP_ID}
            defaultCenter={{ lat: center.latitude, lng: center.longitude }}
            defaultZoom={13.5}
            style={{ width: '100%', height: '100%' }}
            // Night follows the app into Google's dark scheme (a Map ID's cloud style overrides custom style arrays, so
            // Tradehaus's matte style can't be applied here); Day keeps the light map
            colorScheme={document.documentElement.dataset.mode === 'night' ? ColorScheme.DARK : ColorScheme.LIGHT}
            gestureHandling="greedy"
            // no on-map controls (Tradehaus's map shows none); Google's logo and attribution stay, as its terms require
            disableDefaultUI>
            <MapTypeListener onChange={setIsSatelliteView} />
            <MapRings center={center} hoveredPOI={hoveredPOI} />

            {/* the map plots exactly what the list shows; a hovered row lifts its dot */}
            {radiusPOIs
              .filter((poi) => poi.lat != null && poi.lng != null)
              .map((poi) => {
                const key = `${poi.type}-${poi.name}`;
                const active = hoveredRadius === key;
                return (
                  <AdvancedMarker
                    key={key}
                    position={{ lat: poi.lat as number, lng: poi.lng as number }}
                    zIndex={active ? 10 : 1}
                    title={`${poi.name} · ${poi.distanceMi.toFixed(1)} mi`}>
                    <div
                      onMouseEnter={() => setHoveredRadius(key)}
                      onMouseLeave={() => setHoveredRadius(null)}
                      style={{ cursor: 'pointer' }}>
                      <POIMarker color={poi.color} hollow={poi.category === 'hazard'} active={active} />
                    </div>
                  </AdvancedMarker>
                );
              })}

            <AdvancedMarker position={{ lat: center.latitude, lng: center.longitude }} title={propertyAddress}>
              <PropertyMarker />
            </AdvancedMarker>


          </Map>
        )}
      </div>

      <div
        style={{
          ...(isMobile && {
            flex: listOpen ? 1 : 'none',
            overflowY: 'auto',
            minHeight: 0,
          }),
        }}>
        {radiusPOIs.length > 0 && (
          // what's nearby as grouped sections, each an eyebrow over its nearest places (dot, name, distance)
          <>
          {/* the map leads; the places open under its title on demand */}
          <button
            type="button"
            aria-expanded={listOpen}
            onClick={() => setListOpen((v) => !v)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 20px',
              height: 60,
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 17,
              fontWeight: 400,
              letterSpacing: '-0.015em',
              color: 'var(--tx-text)',
            }}>
            Neighborhood
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, letterSpacing: 0, color: 'var(--tx-muted)' }}>
              Points of interest
              <ChevronDown
                size={14}
                style={{ transform: listOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s ease' }}
              />
            </span>
          </button>
          {listOpen && (
          <div
            style={{
              padding: '0 20px 20px',
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
              columnGap: 32,
              rowGap: 18,
            }}>
            {Array.from(new Set(radiusPOIs.map((p) => p.group))).map((group) => (
              <div key={group} style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)' }}>{group}</div>
                {radiusPOIs
                  .filter((p) => p.group === group)
                  .map((poi) => {
                    const key = `${poi.type}-${poi.name}`;
                    return (
                      <div
                        key={key}
                        onMouseEnter={() => setHoveredRadius(key)}
                        onMouseLeave={() => setHoveredRadius(null)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          minWidth: 0,
                          fontSize: 12,
                          fontWeight: 500,
                          color: 'var(--tx-text)',
                          cursor: 'pointer',
                        }}>
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            flexShrink: 0,
                            background: poi.category === 'hazard' ? 'transparent' : poi.color,
                            boxShadow: poi.category === 'hazard' ? `inset 0 0 0 1.5px ${poi.color}` : 'none',
                          }}
                        />
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {poi.name}
                        </span>
                        <span style={{ color: 'var(--tx-muted)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                          {poi.distanceMi.toFixed(1)} mi
                        </span>
                      </div>
                    );
                  })}
              </div>
            ))}
          </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default NeighborhoodMapCard;
